import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Users, UsersRound, UserCog, UserCheck, TrendingUp,
  Mail, MessageCircle, BookOpen, Sparkles, Repeat, Search, ArrowRight,
} from "lucide-react";
import { api } from "../lib/api";
import { Stat } from "../components/Stat";
import { ActivityFeed } from "../components/ActivityFeed";
import { MovementMetrics } from "../components/MovementMetrics";
import { FAITH_STATUSES, STAGES, fmtDate } from "../lib/constants";

interface Summary {
  kpis: { total_people: number; active_groups: number; active_leaders: number; in_groups: number; people_per_leader: number };
  pathway: Record<string, number>;
  leaders: { total: number; active: number; in_training: number; potential: number };
  faith_status: { faith_status: string; count: number }[];
  recent_people: any[];
  training_progress: { id: string; name: string; completed: number; total: number }[];
}

const STAGE_ICONS = {
  outreach: Search,
  invited: Mail,
  joined: BookOpen,
  active: MessageCircle,
  multiplying: Sparkles,
} as const;

export default function Dashboard() {
  const [data, setData] = useState<Summary | null>(null);
  useEffect(() => { api.get<Summary>("/dashboard/summary").then(setData); }, []);
  if (!data) return <div className="text-ink-500">Loading…</div>;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Dashboard</h1>
        <p className="text-ink-500 text-sm">Overview of your discipleship ministry</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Stat value={data.kpis.total_people} label="Total People" icon={<Users className="h-5 w-5" />} />
        <Stat value={data.kpis.active_groups} label="Active Groups" icon={<UsersRound className="h-5 w-5" />} />
        <Stat value={data.kpis.active_leaders} label="Active Leaders" icon={<UserCog className="h-5 w-5" />} />
        <Stat value={data.kpis.in_groups} label="In Groups" icon={<UserCheck className="h-5 w-5" />} />
        <Stat value={data.kpis.people_per_leader.toFixed(1)} label="People per Leader" accent="text-brand" icon={<TrendingUp className="h-5 w-5" />} />
      </div>

      <section className="card p-6">
        <h2 className="font-semibold mb-4">Discipleship Pathway</h2>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {STAGES.map((s) => {
            const total = Math.max(1, data.kpis.total_people);
            const count = data.pathway[s.id] ?? 0;
            const Icon = STAGE_ICONS[s.id as keyof typeof STAGE_ICONS];
            return (
              <Link to={`/people?stage=${s.id}`} key={s.id} className={`rounded-lg p-4 ${s.color} hover:shadow-md transition group`}>
                <div className="flex items-center justify-between">
                  <Icon className="h-4 w-4 opacity-70" />
                  <span className="text-[10px] opacity-60">{Math.round((count / total) * 100)}%</span>
                </div>
                <div className="text-3xl font-semibold mt-3">{count}</div>
                <div className="text-xs font-medium opacity-80 mt-1">{s.label}</div>
              </Link>
            );
          })}
        </div>
      </section>

      <div className="grid md:grid-cols-2 gap-6">
        <section className="card p-6">
          <h2 className="font-semibold mb-4">Faith Status Breakdown</h2>
          <div className="space-y-2">
            {FAITH_STATUSES.map((s) => {
              const c = data.faith_status.find((r) => r.faith_status === s.id)?.count ?? 0;
              return (
                <div key={s.id} className="flex items-center justify-between text-sm py-1">
                  <div className="flex items-center gap-2">
                    <span className={`h-2.5 w-2.5 rounded-full ${s.dot}`} />
                    <span className="text-ink-700">{s.label}</span>
                  </div>
                  <span className="font-semibold">{c}</span>
                </div>
              );
            })}
          </div>
        </section>

        <section className="card p-6">
          <h2 className="font-semibold mb-4">Leader Training Progress</h2>
          {data.training_progress.length === 0 && <div className="text-sm text-ink-500">No leaders yet.</div>}
          <div className="space-y-3">
            {data.training_progress.map((l) => {
              const pct = l.total > 0 ? Math.round((l.completed / l.total) * 100) : 0;
              return (
                <div key={l.id}>
                  <div className="flex justify-between text-sm">
                    <span>{l.name}</span><span className="text-ink-500">{l.completed}/{l.total}</span>
                  </div>
                  <div className="h-2 bg-ink-100 rounded-full overflow-hidden mt-1">
                    <div className="h-full bg-brand transition-all" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      </div>

      <section className="card p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-semibold">Recent People Added</h2>
          <Link to="/people" className="text-xs text-brand hover:underline flex items-center gap-1">View all <ArrowRight className="h-3 w-3" /></Link>
        </div>
        {data.recent_people.length === 0 ? (
          <div className="text-sm text-ink-500">No people yet — head to <Link to="/people" className="text-brand">People</Link> to add the first.</div>
        ) : (
          <div className="divide-y divide-ink-100">
            {data.recent_people.map((p) => (
              <div key={p.id} className="flex items-center justify-between py-2 text-sm">
                <div>
                  <div className="font-medium">{p.name}</div>
                  <div className="text-ink-500 text-xs">{p.source ?? "—"}</div>
                </div>
                <div className="text-right">
                  <div className="text-ink-700">{stageBadgeLabel(p.pathway_stage)}</div>
                  <div className="text-ink-400 text-xs">{fmtDate(p.created_at)}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <div className="grid md:grid-cols-3 gap-4">
        <SummaryCard icon={<Mail />} bg="bg-stage-invited" text="text-blue-900" value={data.pathway.invited ?? 0} title="Moving forward" subtitle="invited & joining DBS" />
        <SummaryCard icon={<BookOpen />} bg="bg-stage-joined" text="text-green-900" value={(data.pathway.joined ?? 0) + (data.pathway.active ?? 0)} title="In DBS" subtitle="actively in DBS groups" />
        <SummaryCard icon={<Repeat />} bg="bg-stage-multiplying" text="text-red-900" value={data.pathway.multiplying ?? 0} title="Multiplication" subtitle="making disciples" />
      </div>

      <MovementMetrics />

      <section className="card p-6">
        <h2 className="font-semibold mb-4">Recent activity</h2>
        <ActivityFeed limit={10} />
      </section>
    </div>
  );
}

function SummaryCard({ icon, bg, text, value, title, subtitle }: any) {
  return (
    <div className={`rounded-xl ${bg} p-5`}>
      <div className={`flex items-center justify-between ${text}`}>
        <span className="opacity-70">{icon}</span>
        <span className="text-3xl font-semibold">{value}</span>
      </div>
      <div className={`text-sm font-medium mt-2 ${text}`}>{title}</div>
      <div className={`text-xs opacity-70 ${text}`}>{subtitle}</div>
    </div>
  );
}

function stageBadgeLabel(s: string) {
  const m = STAGES.find((x) => x.id === s);
  return m?.label ?? s;
}
